<script lang="ts">
const __DsBlock = {
	type: 'input',
	icon: 'lucide:users',
	name: '$t:blocks.input_user_m2m.name',
	description: '$t:blocks.input_user_m2m.description',
	category: 'input',
	tags: ['form-control'],
	options: [
		{ name: 'fieldId', label: '$t:blocks.common.props.field' },
		{ name: 'referencedItemKey', label: '$t:blocks.common.props.referenced_item' },
		{ name: 'disabled', label: '$t:blocks.common.props.disabled' },
		{ name: 'readonly', label: '$t:blocks.common.props.readonly' },
		{ name: 'disabledNotice', label: '$t:blocks.common.props.disabled_notice' },
	],
	hidden: ({ isRoot }) => isRoot,
}
</script>
<script lang="ts" setup>
import type { UserAvatarUser } from '#layers/access/public';
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { AnyItem, CompositePrimaryKey, Filter, GlobalSingleRelationFieldId } from '#layers/data-model/public';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccessDisplayNames, UserAvatar, UserPicker } from '#layers/access/public';
import { defineBlock } from '#layers/block-engine/public';
import RelatedItemListCard from '#layers/blocks/components/Relation/RelatedItemListCard.vue';
import { useM2MDisplayItems } from '#layers/blocks/composables/relation/use-m2m-display-items';
import { useM2MRowActions } from '#layers/blocks/composables/relation/use-m2m-row-actions';
import { useM2MState } from '#layers/blocks/composables/relation/use-m2m-state';
import { getItemPrimaryKey, isSameKey, stringifyStableId, toCompositePrimaryKey } from '#layers/data-model/public';
import { injectFormBlockContext, useM2MPermissions, useRelationFormField } from '#layers/forms/public';
import { ButtonAdd } from '#layers/ui/public';

export interface InputUserM2MBlockProps extends Pick<InputBlockProps, 'disabled' | 'disabledNotice' | 'readonly'> {
	fieldId: GlobalSingleRelationFieldId;
	referencedItemKey: CompositePrimaryKey;
}

const {
	disabled: disabledProp,
	disabledNotice: disabledNoticeProp,
	fieldId,
} = defineProps<InputUserM2MBlockProps>();

;

const { t } = useI18n();
const { getUserDisplayName } = useAccessDisplayNames();

const relationFieldContext = useRelationFormField<'to-many'>(() => ({ disabled: disabledProp, disabledNotice: disabledNoticeProp, fieldId }));
const { disabled, disabledNotice } = relationFieldContext;

const pageSize = ref(25);

const m2mState = useM2MState({
	...relationFieldContext,
	pageSize,
	additionalSelection: { fields: ['avatarId', 'email'] },
});
const {
	items,
	targetCollection,
	m2mStructure,
	connectTarget,
	disconnectTarget,
	isTargetPendingCreate,
	connectedTargetsFilter,
	pendingDeleteTargetKeys,
	getTargetKeyFromJunction,
	pageNumber,
	totalItemsCount,
	currentItemsCount,
	isLoading,
	junctionPrimaryKeyFields,
} = m2mState;

const { displayItems } = useM2MDisplayItems<UserAvatarUser>(m2mState);
const { removeJunction, undoJunction } = useM2MRowActions(m2mState);

const formBlockContext = injectFormBlockContext(null);
const permissions = useM2MPermissions({
	m2mStructure,
	writeContext: () => formBlockContext?.writeContext.value,
});

const popoverOpen = ref(false);

// The server-side "not already connected" filter. While the popover is closed it tracks
// `connectedTargetsFilter` live, so the eagerly primed member list already omits connected members
// before the first open (otherwise the picker flashes the full list, then shortens once the filtered
// fetch resolves). While open it is frozen: `connectedTargetsFilter` also excludes pending creates,
// so a live filter would make a row vanish the instant it was toggled. Closing resyncs it, folding in
// whatever was assigned in the meantime.
const pickerFilter = ref<Filter>(connectedTargetsFilter.value);

watch([connectedTargetsFilter, popoverOpen], ([filter, open]) => {
	if (!open) pickerFilter.value = filter;
});

// Target keys of members that have a real DB junction but are shown in the picker because their
// delete is pending (connectedTargetsFilter re-includes them). Snapshotted when the popover opens,
// matching the frozen pickerFilter, so an undone delete reads as connected again instead of luring a
// second click that would emit a duplicate connect for a row that still exists in the DB.
const connectedInDbKeys = ref(new Set<string>());

watch(popoverOpen, (open) => {
	if (open) {
		connectedInDbKeys.value = new Set(
			pendingDeleteTargetKeys.value.map((key) => stringifyStableId(key)),
		);
	}
});

// Whether the member will be connected after save: a pending create, or a real DB junction whose
// delete is not currently pending (this also covers a delete that was just undone in this session).
function isConnectedByKey(targetKey: CompositePrimaryKey): boolean {
	if (isTargetPendingCreate(targetKey)) return true;
	if (!connectedInDbKeys.value.has(stringifyStableId(targetKey))) return false;
	return !pendingDeleteTargetKeys.value.some((key) => isSameKey(key, targetKey));
}

function isMemberConnected(member: UserAvatarUser): boolean {
	if (!targetCollection.value) return false;
	return isConnectedByKey(toCompositePrimaryKey(member.id, targetCollection.value));
}

function toggleMember(memberId: string) {
	if (!targetCollection.value) return;

	const targetKey = toCompositePrimaryKey(memberId, targetCollection.value);

	if (isConnectedByKey(targetKey)) {
		disconnectTarget(targetKey);
	}
	else {
		connectTarget(targetKey);
	}
}

function findJunctionByTargetKey(targetKey: CompositePrimaryKey): AnyItem | undefined {
	return items.value.find((junctionItem) => {
		const key = getTargetKeyFromJunction(junctionItem);
		return key && isSameKey(key, targetKey);
	});
}

function disconnectByKey(targetKey: CompositePrimaryKey) {
	const junctionItem = findJunctionByTargetKey(targetKey);
	if (junctionItem) removeJunction(junctionItem);
}

function handleRemove(targetData: AnyItem) {
	if (!targetCollection.value) return;
	disconnectByKey(getItemPrimaryKey(targetData, targetCollection.value));
}

function handleUndo(targetData: AnyItem) {
	if (!targetCollection.value) return;

	const junctionItem = findJunctionByTargetKey(getItemPrimaryKey(targetData, targetCollection.value));
	if (junctionItem) undoJunction(junctionItem);
}
</script>

<template>
	<DisplayTemplateConfigurationError
		v-if="!targetCollection?.id"
		:error="$t('blocks.common.relation_configuration_error')"
	/>
	<DisplayTemplateConfigurationError
		v-else-if="junctionPrimaryKeyFields?.length === 0"
		:error="$t('blocks.common.junction_missing_primary_key')"
	/>
	<RelatedItemListCard
		v-else
		:items="displayItems"
		:collection-id="targetCollection?.id"
		:state-key="fieldId"
		:disabled
		:disabled-notice
		:readonly
		:aria-readonly="readonly"
		:show-create="false"
		:show-add-existing="false"
		:show-edit="false"
		count-label="common.n_users"
		:pagination="{
			page: pageNumber,
			pageSize,
			totalItems: totalItemsCount,
			currentItems: currentItemsCount,
		}"
		:loading="isLoading"
		:permissions="{ canDisconnect: permissions.canDisconnect.value }"
		@remove="handleRemove"
		@undo="handleUndo"
		@update:page="pageNumber = $event"
		@update:page-size="pageSize = $event"
	>
		<template #actions>
			<UserPicker
				v-model:open="popoverOpen"
				multiple
				content-class="w-56"
				:is-selected="isMemberConnected"
				:filter="pickerFilter"
				@select="toggleMember"
			>
				<ButtonAdd
					size="sm"
					variant="ghost"
					:label="t('blocks.input_user_m2m.add')"
					:disabled="disabled || !permissions.canConnect.value"
				/>
			</UserPicker>
		</template>

		<template #item="{ item }">
			<div class="flex h-full min-w-0 items-center gap-2">
				<UserAvatar :user="item.data" class="size-7 flex-none" />
				<span class="truncate text-sm font-medium text-default">{{ getUserDisplayName(item.data) }}</span>
			</div>
		</template>
	</RelatedItemListCard>
</template>
